import { createContext, useContext, useMemo, useState } from 'react'

export interface PinStore {
  /** Where this artifact is pinned, or null. */
  pinnedTo: (title: string) => string | null
  setPin: (title: string, target: string | null) => void
  /** The artifacts pinned to one destination, in the order they were pinned. */
  pinnedFor: (target: string) => string[]
}

const PinnedContext = createContext<PinStore | null>(null)

function makeStore(pins: string[][], setPins: React.Dispatch<React.SetStateAction<string[][]>>): PinStore {
  return {
    pinnedTo: (title) => pins.find(([t]) => t === title)?.[1] ?? null,
    setPin: (title, target) =>
      setPins((was) => {
        const rest = was.filter(([t]) => t !== title)
        return target ? [...rest, [title, target]] : rest
      }),
    pinnedFor: (target) => pins.filter(([, t]) => t === target).map(([title]) => title),
  }
}

/** Shared so the Artifacts tab and a product's Overview see the same pins. */
export function PinnedArtifactsProvider({ children }: { children: React.ReactNode }) {
  const [pins, setPins] = useState<string[][]>([])
  const store = useMemo(() => makeStore(pins, setPins), [pins])

  return <PinnedContext value={store}>{children}</PinnedContext>
}

/**
 * The shared store where a placement provides one. Surfaces that show the
 * agent without a product page fall back to their own, so the pin control
 * still works there rather than looking broken.
 */
export function usePinStore(): PinStore {
  const shared = useContext(PinnedContext)
  const [pins, setPins] = useState<string[][]>([])
  const own = useMemo(() => makeStore(pins, setPins), [pins])

  return shared ?? own
}
